import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Calendar, CheckCircle2, Eye, EyeOff, Shield, Trash2 } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card.tsx'
import { Button } from '../ui/button.tsx'
import { Alert, AlertDescription } from '../ui/alert.tsx'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.tsx'
import { Input } from '../ui/input.tsx'
import { Label } from '../ui/label.tsx'
import {
  getUserAccountStatus,
  requestAccountDeletion,
  restoreAccount,
} from '../../lib/account.ts'
import { formatDate } from '../../lib/utils.ts'
import type { UserAccountStatus } from '../../lib/api/types.ts'

export function AccountDeletionSection() {
  const { t } = useTranslation()
  const [accountStatus, setAccountStatus] = useState<UserAccountStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Delete dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [confirmationText, setConfirmationText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Restore state
  const [isRestoring, setIsRestoring] = useState(false)
  const [restoreError, setRestoreError] = useState<string | null>(null)
  const [restoreSuccess, setRestoreSuccess] = useState(false)

  useEffect(() => {
    loadAccountStatus()
  }, [])

  const loadAccountStatus = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const status = await getUserAccountStatus()
      setAccountStatus(status)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('account.deletion.loadError'))
    } finally {
      setIsLoading(false)
    }
  }

  const resetDialogState = () => {
    setPassword('')
    setShowPassword(false)
    setConfirmationText('')
    setDeleteError(null)
  }

  const handleDeleteClick = () => {
    resetDialogState()
    setIsDeleteDialogOpen(true)
  }

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      resetDialogState()
    }
    setIsDeleteDialogOpen(open)
  }

  const handleConfirmDelete = async () => {
    if (confirmationText !== 'DELETE') {
      setDeleteError(t('account.deletion.confirmationMismatch'))
      return
    }

    if (!password) {
      setDeleteError(t('account.deletion.enter_password'))
      return
    }

    setIsDeleting(true)
    setDeleteError(null)

    try {
      const result = await requestAccountDeletion(password, confirmationText)

      // Close dialog and show pending-deletion banner
      setIsDeleteDialogOpen(false)
      resetDialogState()
      setAccountStatus({
        scheduled_for_deletion_at: result.scheduled_for_deletion_at,
        days_until_deletion: result.days_until_deletion,
      })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('account.deletion.deleteError')
      // Check for specific password-related errors
      if (
        errorMessage.toLowerCase().includes('invalid') ||
        errorMessage.toLowerCase().includes('password') ||
        errorMessage.toLowerCase().includes('incorrect')
      ) {
        setDeleteError(t('account.deletion.invalid_password'))
      } else {
        setDeleteError(errorMessage)
      }
    } finally {
      setIsDeleting(false)
    }
  }

  const handleRestoreAccount = async () => {
    setIsRestoring(true)
    setRestoreError(null)
    setRestoreSuccess(false)

    try {
      await restoreAccount()
      setAccountStatus({
        scheduled_for_deletion_at: null,
        days_until_deletion: null,
      })
      setRestoreSuccess(true)
      // Clear success message after 5 seconds
      setTimeout(() => setRestoreSuccess(false), 5000)
    } catch (err) {
      setRestoreError(err instanceof Error ? err.message : t('account.deletion.restoreError'))
    } finally {
      setIsRestoring(false)
    }
  }

  const scheduledDeletionDate = accountStatus?.scheduled_for_deletion_at
  const daysUntilDeletion = accountStatus?.days_until_deletion
  const isPendingDeletion = scheduledDeletionDate != null
  const isConfirmationValid = confirmationText === 'DELETE'
  const isFormValid = isConfirmationValid && password.length > 0

  if (isLoading) {
    return (
      <Card className="border-[#E9E8F1]">
        <CardHeader>
          <CardTitle className="text-lg">{t('account.deletion.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-[#6D6D6D]">{t('common.loading')}</div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className="border-[#E9E8F1]">
        <CardHeader>
          <CardTitle className="text-lg">{t('account.deletion.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      {isPendingDeletion ? (
        <Card className="border-[#E9E8F1] overflow-hidden">
          {/* Amber accent bar */}
          <div className="h-1 bg-gradient-to-r from-amber-400 to-amber-500" />

          <CardHeader className="pb-3">
            <div className="flex items-center gap-2.5">
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center">
                <AlertTriangle className="h-[18px] w-[18px] text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-lg leading-tight">
                  {t('account.deletion.pendingTitle')}
                </CardTitle>
                <CardDescription className="mt-0.5">
                  {t('account.deletion.pendingMessage', {
                    date: formatDate(scheduledDeletionDate),
                    days: daysUntilDeletion ?? 0,
                  })}
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-4 pt-0">
            {/* Deletion date info row */}
            <div className="flex items-center gap-3 rounded-lg bg-amber-50/60 border border-amber-200/50 px-4 py-3">
              <Calendar className="h-4 w-4 text-amber-600 flex-shrink-0" />
              <div className="flex items-center justify-between flex-1 min-w-0">
                <span className="text-sm text-amber-800">{t('account.deletion.deletionDate')}</span>
                <span className="text-sm font-semibold text-amber-900">{formatDate(scheduledDeletionDate)}</span>
              </div>
            </div>

            {restoreError && (
              <Alert variant="destructive">
                <AlertDescription>{restoreError}</AlertDescription>
              </Alert>
            )}

            <Button
              onClick={handleRestoreAccount}
              disabled={isRestoring}
              className="w-full bg-[#26247B] hover:bg-[#1a1860] text-white"
            >
              <Shield className="h-4 w-4 mr-2" />
              {isRestoring
                ? t('account.deletion.restoring')
                : t('account.deletion.cancelDeletion')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[#E9E8F1]">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-[#6D6D6D]" />
              <CardTitle className="text-lg">
                {t('account.deletion.title')}
              </CardTitle>
            </div>
            <CardDescription>
              {t('account.deletion.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {restoreSuccess && (
              <div className="flex items-center gap-2.5 rounded-lg bg-emerald-50 border border-emerald-200/50 px-4 py-3">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                <p className="text-sm text-emerald-800">
                  {t('account.deletion.cancelledSuccess')}
                </p>
              </div>
            )}
            <p className="text-sm text-[#6D6D6D]">
              {t('account.deletion.warning')}
            </p>
            <Button
              variant="destructive"
              onClick={handleDeleteClick}
              className="w-full sm:w-auto"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t('account.deletion.deleteButton')}
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={isDeleteDialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#CD5B52]">
              <AlertTriangle className="h-5 w-5" />
              {t('account.deletion.dialogTitle')}
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              <p>{t('account.deletion.dialogDescription')}</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>{t('account.deletion.dialogPoint1')}</li>
                <li>{t('account.deletion.dialogPoint2')}</li>
                <li>{t('account.deletion.dialogPoint3')}</li>
              </ul>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Password Input */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                {t('account.deletion.enter_password')}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('common.password')}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6D6D6D] hover:text-[#333]"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* DELETE confirmation */}
            <div className="space-y-2">
              <Label htmlFor="confirmation" className="text-sm font-medium">
                {t('account.deletion.confirmLabel')}
              </Label>
              <Input
                id="confirmation"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder="DELETE"
                className={`${
                  confirmationText.length > 0 && !isConfirmationValid
                    ? 'border-[#CD5B52] focus-visible:ring-[#CD5B52]/20'
                    : ''
                }`}
              />
              {confirmationText.length > 0 && !isConfirmationValid && (
                <p className="text-xs text-[#CD5B52]">
                  {t('account.deletion.typeDeleteExact')}
                </p>
              )}
            </div>

            {deleteError && (
              <Alert variant="destructive">
                <AlertDescription>{deleteError}</AlertDescription>
              </Alert>
            )}

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setIsDeleteDialogOpen(false)}
                disabled={isDeleting}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
                disabled={isDeleting || !isFormValid}
              >
                {isDeleting
                  ? t('account.deletion.deleting')
                  : t('account.deletion.confirmDelete')}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
